import { motion } from 'framer-motion';
import { Instagram, Facebook, Phone, Mail, MapPin, MessageCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_luxury-drape/artifacts/4nl3m339_Gemini_Generated_Image_2w5nsu2w5nsu2w5n-removebg-preview.png";
const WHATSAPP_NUMBER = "919535834444";

const footerLinks = {
  collections: [
    { name: 'Sarees', href: '#collections' },
    { name: 'Lehengas', href: '#collections' },
    { name: 'Gowns', href: '#collections' },
    { name: 'Ethnic Wear', href: '#collections' },
    { name: 'Dress Materials', href: '#collections' },
    { name: 'Ready-Made', href: '#collections' },
  ],
  customerCare: [
    { name: 'Shipping Policy', href: '#' },
    { name: 'Return Policy', href: '#' },
    { name: 'Size Guide', href: '#' },
    { name: 'FAQs', href: '#' },
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Contact Us', href: '#contact' },
  ],
  social: [
    { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/devanandvestment/' },
    { name: 'Facebook', icon: Facebook, href: '#' },
    { name: 'WhatsApp', icon: MessageCircle, href: `https://wa.me/${WHATSAPP_NUMBER}` },
  ],
};

// Gold Decorative Pattern SVG
const GoldPattern = () => (
  <svg className="w-full h-8" viewBox="0 0 1200 32" preserveAspectRatio="none">
    <defs>
      <linearGradient id="goldGradientFooter" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
        <stop offset="20%" stopColor="#D4AF37" stopOpacity="0.5" />
        <stop offset="50%" stopColor="#D4AF37" stopOpacity="1" />
        <stop offset="80%" stopColor="#D4AF37" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
      </linearGradient>
    </defs>
    <path
      d="M0,16 Q300,0 600,16 T1200,16"
      stroke="url(#goldGradientFooter)"
      strokeWidth="1"
      fill="none"
    />
    <path
      d="M0,20 Q300,32 600,20 T1200,20"
      stroke="url(#goldGradientFooter)"
      strokeWidth="0.5"
      fill="none"
    />
    {/* Decorative diamonds */}
    <g fill="#D4AF37">
      <rect x="595" y="13" width="10" height="10" transform="rotate(45 600 18)" />
      <rect x="295" y="11" width="6" height="6" transform="rotate(45 298 14)" />
      <rect x="895" y="11" width="6" height="6" transform="rotate(45 898 14)" />
    </g>
  </svg>
);

export const Footer = () => {
  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer 
      id="contact" 
      className="bg-[#800020] relative"
      data-testid="footer-section"
    >
      {/* Gold Decorative Pattern */}
      <GoldPattern />

      {/* Main Footer Content */}
      <div className="max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <motion.img
              src={LOGO_URL}
              alt="Devanand Vestment"
              className="h-16 mb-6"
              whileHover={{ scale: 1.05 }}
            />
            <p className="font-body text-[#FFFDD0]/80 leading-relaxed mb-6">
              Elegance Woven in Tradition. Discover timeless Indian ethnic wear 
              crafted with love and precision.
            </p>
            {/* Social Icons */}
            <div className="flex gap-4">
              {footerLinks.social.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#800020] transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid={`footer-social-${social.name.toLowerCase()}`}
                >
                  <social.icon size={18} />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Collections Column */}
          <div>
            <h4 className="font-heading text-xl text-[#FFFDD0] mb-6">Collections</h4>
            <ul className="space-y-3">
              {footerLinks.collections.map((link) => (
                <li key={link.name}>
                  <motion.button
                    onClick={() => scrollToSection(link.href)}
                    className="font-body text-[#FFFDD0]/70 hover:text-[#D4AF37] transition-colors text-left"
                    whileHover={{ x: 5 }}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </motion.button>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Care Column */}
          <div>
            <h4 className="font-heading text-xl text-[#FFFDD0] mb-6">Customer Care</h4>
            <ul className="space-y-3">
              {footerLinks.customerCare.map((link) => (
                <li key={link.name}>
                  <motion.button
                    onClick={() => scrollToSection(link.href)}
                    className="font-body text-[#FFFDD0]/70 hover:text-[#D4AF37] transition-colors text-left"
                    whileHover={{ x: 5 }}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </motion.button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="font-heading text-xl text-[#FFFDD0] mb-6">Get in Touch</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 font-body text-[#FFFDD0]/70 hover:text-[#D4AF37] transition-colors"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle size={18} className="text-[#D4AF37]" />
                  <span>+91 95358 34444</span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+919535834444"
                  className="flex items-center gap-3 font-body text-[#FFFDD0]/70 hover:text-[#D4AF37] transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone size={18} className="text-[#D4AF37]" />
                  <span>+91 95358 34444</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:arvindsutrave444@gmail.com"
                  className="flex items-center gap-3 font-body text-[#FFFDD0]/70 hover:text-[#D4AF37] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail size={18} className="text-[#D4AF37]" />
                  <span>arvindsutrave444@gmail.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 font-body text-[#FFFDD0]/70">
                  <MapPin size={18} className="text-[#D4AF37] flex-shrink-0 mt-1" />
                  <span>KALABURAGI<br />Shop No: 1-10/B KHUBA COMPLEX,<br />KHUBA PLOT GULBARGA KALABURAGI<br />KALABURAGI, KARNATAKA 585102<br />India</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#D4AF37]/20">
        <div className="max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-body text-sm text-[#FFFDD0]/60">
              © {new Date().getFullYear()} Devanand Vestment. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-[#FFFDD0]/60 text-sm">
              <span>Designed by</span>
              <span className="text-[#D4AF37] font-medium">Viral Boostup</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <motion.a
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi! I'm visiting your website and would like to know more about your collections.")}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2 }}
        data-testid="floating-whatsapp-btn"
      >
        <MessageCircle className="text-white" size={28} />
      </motion.a>
    </footer>
  );
};
